import { useState } from "react";

import { 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Shield,
  Users
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { useAdminEdgeFunction } from "@/hooks/useAdminEdgeFunction";

interface MigrationResult {
  user_id: string;
  email: string;
  success: boolean;
  new_code?: string;
  error?: string;
}

interface MigrationResponse {
  success: boolean;
  message: string;
  total_users: number;
  migrated_count: number;
  failed_count: number;
  results: MigrationResult[];
}

export const CommunicationCodeMigrationTool = () => {
  const { invokeAdmin } = useAdminEdgeFunction();
  const [migrating, setMigrating] = useState(false);
  const [migrationData, setMigrationData] = useState<MigrationResponse | null>(null);

  const handleMigration = async () => {
    if (!confirm('This will assign auto-generated codes to all users with CODE-NOT-SET. Continue?')) {
      return;
    }

    setMigrating(true);
    setMigrationData(null);

    try {
      const { data, error } = await invokeAdmin('migrate-communication-codes', {});

      if (error) throw error;

      setMigrationData(data as MigrationResponse);
      
      if (data.migrated_count > 0) {
        toast.success(`Successfully migrated ${data.migrated_count} users`);
      } else {
        toast.info(data.message);
      }
    } catch (error: any) {
      console.error('Migration error:', error);
      toast.error(error.message || 'Failed to run migration');
    } finally {
      setMigrating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10">
            <Shield className="h-6 w-6 text-blue-500" />
          </div>
          <div className="flex-1">
            <CardTitle>Communication Code Migration</CardTitle>
            <CardDescription>
              Migrate users from CODE-NOT-SET to auto-generated security codes
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Migration Process</AlertTitle>
          <AlertDescription className="space-y-2">
            <p className="text-sm">
              This tool will:
            </p>
            <ul className="list-disc pl-4 space-y-1 text-sm">
              <li>Find all users with communication_code = 'CODE-NOT-SET'</li>
              <li>Generate unique auto-generated codes for each user</li>
              <li>Mark codes as auto-generated (requiring user confirmation)</li>
              <li>Log the migration in communication_code_history</li>
              <li>Show a banner to these users prompting them to accept or customize</li>
            </ul>
            <p className="text-sm font-medium mt-2">
              Users will see a banner on next login to either keep or customize their code.
            </p>
          </AlertDescription>
        </Alert>

        <Button
          onClick={handleMigration}
          disabled={migrating}
          className="w-full"
          size="lg"
        >
          {migrating ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Running Migration...
            </>
          ) : (
            <>
              <Users className="mr-2 h-4 w-4" />
              Run Migration
            </>
          )}
        </Button>

        {migrationData && (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Migration Results</h3>
              <div className="flex gap-2">
                <Badge variant="outline" className="bg-green-500/10 text-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {migrationData.migrated_count} Success
                </Badge>
                {migrationData.failed_count > 0 && (
                  <Badge variant="outline" className="bg-red-500/10 text-red-600">
                    <XCircle className="h-3 w-3 mr-1" />
                    {migrationData.failed_count} Failed
                  </Badge>
                )}
              </div>
            </div>

            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertTitle>{migrationData.message}</AlertTitle>
              <AlertDescription>
                {migrationData.total_users === 0 ? (
                  <p className="text-sm">All users already have communication codes set.</p>
                ) : (
                  <p className="text-sm">
                    Processed {migrationData.total_users} users. 
                    Users will see a banner on next login to confirm or customize their codes.
                  </p>
                )}
              </AlertDescription>
            </Alert>

            {migrationData.results.length > 0 && (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                <h4 className="text-sm font-medium text-muted-foreground">
                  Individual Results:
                </h4>
                {migrationData.results.map((result, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{result.email}</p>
                      {result.success && result.new_code && (
                        <p className="text-xs text-muted-foreground font-mono">
                          {result.new_code}
                        </p>
                      )}
                      {!result.success && result.error && (
                        <p className="text-xs text-red-600 dark:text-red-400">
                          {result.error}
                        </p>
                      )}
                    </div>
                    <div className="ml-2">
                      {result.success ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
